<script setup lang="ts" generic="T">
import { computed } from 'vue'
import { ElTable, ElTableColumn } from 'element-plus'

import { useListError } from '@/shared/lib'
import { Alert } from '@/shared/ui/Alert'
import { Button } from '@/shared/ui/Button'

// The wrapper below is not the attribute target — every attribute a caller passes is
// meant for the grid, and `v-bind="$attrs"` puts it there.
defineOptions({ inheritAttrs: false })

/**
 * Every list drawn as a grid: rows through `data`, columns as `TableColumn`s in the default
 * slot. A list of thousands of rows with no paging is `VirtualTable`; a table of records is
 * this one.
 *
 * It is generic over the row, so `#expanded="{ row }"` is typed where it is rendered —
 * declare `data` as an array, never a ref, and never `any[]`.
 *
 * What it does on its own, so a widget does not: it draws the reason and a "Try again" when
 * the list was refused (announced by `useEntityTable`), and a banner over stale rows when a
 * *refetch* was refused. The `#empty` slot is for "you have nothing yet" and is shown only
 * when that is really what happened — `useTableEmptySlot` decides that.
 */
const props = withDefaults(
  defineProps<{
    data: T[]
    loading?: boolean
    border?: boolean
    stripe?: boolean
    expandable?: boolean
    /**
     * A metric grid rather than a list of records: smaller type and tighter cells, so a
     * table of fifteen figures fits a card instead of scrolling sideways past the last
     * four columns. Only the dashboard's widest panels need it; a normal list does not.
     */
    dense?: boolean
    /**
     * Which field identifies a row, for the expand store and for element-plus's own
     * row bookkeeping.
     *
     * `id` fits every entity table, but **a grid whose rows have no `id` must pass its
     * own**: element-plus computes identity as `` `${row[rowKey]}` ``, so rows without one
     * all key to the string `"undefined"` — expanding a single row then reads back as every
     * row expanded.
     */
    rowKey?: string
  }>(),
  { expandable: false, dense: false, rowKey: 'id' }
)

/**
 * The rows, defended once here rather than in every table widget: the guard is about what
 * the API can actually answer, not about what the prop is declared as.
 */
const rows = computed(() => (Array.isArray(props.data) ? props.data : []))

defineSlots<{
  default?(): unknown
  empty?(): unknown
  expanded?(props: { row: T }): unknown
}>()

/**
 * Why the rows are missing, when they are missing because the request failed — announced by
 * the list hook, so the table says "could not load" with a way to ask again instead of
 * showing the caller's "nothing here yet".
 */
const listError = useListError()
</script>

<template>
  <div class="table" :class="{ 'table--dense': dense }">
    <!--
      A refused *refetch* cannot reach the `#empty` slot: the previous page's rows are kept, so
      element-plus never renders its empty area and the table would go on showing stale rows as
      though they were current.
    -->
    <Alert
      v-if="listError && rows.length"
      type="warning"
      :closable="false"
      show-icon
      class="table__stale"
    >
      {{ listError.message }} Showing the last rows that loaded.
      <Button link type="primary" size="small" @click="listError.retry()">
        Try again
      </Button>
    </Alert>

    <ElTable
      v-loading="loading"
      :data="rows"
      :border="border"
      :stripe="stripe"
      :row-key="rowKey"
      style="width: 100%"
      v-bind="$attrs"
    >
      <template #empty>
        <div v-if="listError" class="table__error">
          <span>{{ listError.message }}</span>
          <Button type="primary" size="small" @click="listError.retry()">
            Try again
          </Button>
        </div>
        <slot v-else name="empty" />
      </template>

      <!--
        `$index` is -1 on the pass element-plus makes over this column looking for nested
        columns: `table-column/index.ts` render() calls the slot with
        `{ row: {}, column: {}, $index: -1 }` and keeps the component vnodes that come back
        in the table's `.hidden-columns`. An expanded row here holds a whole widget, and
        building one for a row that does not exist is what this refuses. Real rows start at 0.

        It changes nothing on screen — element-plus drops the vnodes that pass returns — so it is
        a guard against the call, not a fix for a duplicate.
      -->
      <ElTableColumn v-if="expandable" type="expand" :width="dense ? 32 : 48">
        <template #default="scope">
          <slot v-if="scope.$index >= 0" name="expanded" :row="scope.row" />
        </template>
      </ElTableColumn>
      <slot />
    </ElTable>
  </div>
</template>

<style lang="scss" scoped>
/* Element Plus prints a table header in `--el-text-color-secondary`, the same grey it uses
   for placeholder text, so a column name reads as disabled beside its own values. The
   header is a label, not a hint. */
.table :deep(.el-table) {
  --el-table-header-text-color: var(--el-text-color-regular);
}

.table__stale {
  margin-bottom: 8px;
}

.table__error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: var(--el-color-danger);
}

/* Dense: the two font sizes and the cell padding of a metric grid — density only, so a
   header keeps the weight it has in every other table. It has to reach into element-plus's
   own cells, which is why it lives here and not in the widget. */
.table--dense :deep(.el-table) {
  font-size: 12px;
}

.table--dense :deep(.el-table th.el-table__cell) {
  font-size: 11px;
}

.table--dense :deep(.el-table .cell) {
  padding-right: 6px;
  padding-left: 6px;
}

/* A sortable column's custom `#header` slot is free to render a block-level layout (an
   `Inline`, say), which then claims the full width of `.cell` and pushes element-plus's
   own `.caret-wrapper` onto a second line instead of sitting beside it. */
:deep(.el-table__header) th .cell {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
}

/* ...and the flex above is why these two exist: element-plus aligns a header through
   `text-align` on the `th`, which a flex container ignores, so every `align="right"`
   column drew its label hard left while its numbers sat right. */
:deep(.el-table__header) th.is-right .cell {
  justify-content: flex-end;
}

:deep(.el-table__header) th.is-center .cell {
  justify-content: center;
}
</style>
